import type { ReactNode } from "react";
import { Dialog as D } from "radix-ui";
import { X } from "lucide-react";
import { Button } from "./ui.tsx";

/** Accessible modal: focus trap, Escape/overlay close, labelled title (Radix). */
export function Modal({
  open, onOpenChange, title, description, children, footer, wide,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  return (
    <D.Root open={open} onOpenChange={onOpenChange}>
      <D.Portal>
        <D.Overlay className="fixed inset-0 z-40 bg-brand/45 backdrop-blur-[2px] animate-fade-in" />
        <D.Content
          className={`fixed top-1/2 left-1/2 z-50 max-h-[calc(100dvh-2rem)] w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-panel border border-line bg-surface p-6 shadow-e3 animate-scale-in ${wide ? "max-w-2xl" : "max-w-lg"}`}
          aria-describedby={description ? undefined : undefined}
        >
          <div className="flex items-start justify-between gap-4">
            <D.Title className="text-lg font-bold">{title}</D.Title>
            <D.Close asChild>
              <button type="button" aria-label="Fechar" className="elevate -m-1 rounded-ctl p-1 text-muted hover:text-ink">
                <X className="size-5" aria-hidden />
              </button>
            </D.Close>
          </div>
          {description ? <D.Description className="mt-1 text-sm text-muted">{description}</D.Description> : <D.Description className="sr-only">{title}</D.Description>}
          <div className="mt-3">{children}</div>
          {footer && <div className="mt-5 flex flex-wrap justify-end gap-2">{footer}</div>}
        </D.Content>
      </D.Portal>
    </D.Root>
  );
}

export function ConfirmDialog({
  open, onOpenChange, title, children, confirmLabel, onConfirm, loading, danger,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  children?: ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  loading?: boolean;
  danger?: boolean;
}) {
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={loading}>Cancelar</Button>
          <Button variant={danger ? "danger" : "primary"} onClick={onConfirm} loading={loading}>{confirmLabel}</Button>
        </>
      }
    >
      {children}
    </Modal>
  );
}
